import { Injectable, computed, inject } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PlatformPauseDuration } from '../enums/platform-pause-duration.enum';
import { PlatformRecommendationMode } from '../enums/platform-recommendation-mode.enum';
import {
  PLATFORM_DEFAULT_SETTINGS,
  PlatformSettings,
} from '../models/platform.models';
import { PlatformAuthService } from './platform-auth.service';
import { PlatformCandidateApiService } from './platform-candidate-api.service';

@Injectable({
  providedIn: 'root',
})
export class PlatformSettingsService {
  private readonly auth = inject(PlatformAuthService);
  private readonly api = inject(PlatformCandidateApiService);

  readonly settings = computed((): PlatformSettings => {
    const me = this.auth.session();
    if (!me) {
      return PLATFORM_DEFAULT_SETTINGS;
    }
    return {
      recommendationMode: me.recommendationsPaused
        ? PlatformRecommendationMode.PAUSE
        : PlatformRecommendationMode.RECEIVE,
      pauseDuration: PLATFORM_DEFAULT_SETTINGS.pauseDuration,
      pausedUntil: me.recommendationPausedUntil,
      pausedAt: null,
      notifyTalentpoolInvitation: me.notifyTalentpoolInvitation,
      notifyJobRecommendation: me.notifyJobRecommendation,
      notifyApplicationStatus: me.notifyApplicationStatus,
    };
  });

  readonly recommendationsPaused = computed(
    () => this.settings().recommendationMode === PlatformRecommendationMode.PAUSE
  );

  /**
   * Persists notification and recommendation-visibility settings.
   * @param settings - Settings snapshot to store
   * @returns Observable that completes after session refresh
   */
  save(settings: PlatformSettings): Observable<unknown> {
    const notifications = this.api.updateNotifications({
      notifyTalentpoolInvitation: settings.notifyTalentpoolInvitation,
      notifyJobRecommendation: settings.notifyJobRecommendation,
      notifyApplicationStatus: settings.notifyApplicationStatus,
    });
    const pause =
      settings.recommendationMode === PlatformRecommendationMode.PAUSE
        ? this.api.pauseRecommendations(settings.pauseDuration)
        : this.api.pauseRecommendations(null);
    return forkJoin([notifications, pause]).pipe(
      switchMap(() => this.auth.refreshMe())
    );
  }

  /**
   * Calculates the pause end date from a duration.
   * @param duration - Selected pause duration
   * @param from - Start date, defaults to now
   * @returns ISO timestamp or null when pause has no end date
   */
  computePausedUntil(
    duration: PlatformPauseDuration,
    from: Date = new Date()
  ): string | null {
    const months = this.monthsForDuration(duration);
    if (months == null) {
      return null;
    }
    const until = new Date(from);
    until.setMonth(until.getMonth() + months);
    return until.toISOString();
  }

  /**
   * Builds the persisted snapshot from the settings form, keeping an existing end date when unchanged.
   * @param next - Form values without timestamps
   * @param previous - Currently stored settings
   * @returns Settings including pause timestamps
   */
  withPauseDates(
    next: Omit<PlatformSettings, 'pausedUntil' | 'pausedAt'>,
    previous: PlatformSettings
  ): PlatformSettings {
    if (next.recommendationMode !== PlatformRecommendationMode.PAUSE) {
      return {
        ...next,
        pausedUntil: null,
        pausedAt: null,
      };
    }
    const durationUnchanged =
      previous.recommendationMode === PlatformRecommendationMode.PAUSE &&
      previous.pauseDuration === next.pauseDuration;
    if (durationUnchanged) {
      return {
        ...next,
        pausedUntil: previous.pausedUntil,
        pausedAt: previous.pausedAt,
      };
    }
    const pausedAt = new Date();
    return {
      ...next,
      pausedAt: pausedAt.toISOString(),
      pausedUntil: this.computePausedUntil(next.pauseDuration, pausedAt),
    };
  }

  /**
   * Maps a pause duration to a month count.
   * @param duration - Selected pause duration
   * @returns Number of months or null for an open-ended pause
   */
  private monthsForDuration(duration: PlatformPauseDuration): number | null {
    switch (duration) {
      case PlatformPauseDuration.THREE_MONTHS:
        return 3;
      case PlatformPauseDuration.SIX_MONTHS:
        return 6;
      case PlatformPauseDuration.TWELVE_MONTHS:
        return 12;
      case PlatformPauseDuration.UNTIL_ENABLED:
        return null;
      default:
        return 6;
    }
  }
}
